import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, StatusBanner, useToast } from '@shared/ui';
import type { AssetBalanceDto, WithdrawalLimitsDto, WithdrawalUserDto } from '@shared/api/contracts';
import { isActiveWithdrawalState, mapWithdrawalDtoToEntity } from '@entities/withdrawal';
import { useAuth } from '@entities/auth';
import { readIntentSession } from '../../lib/intent-session';
import type { WithdrawalNetwork } from '@shared/config/networks';
import { validateUsdtAddress } from '@shared/lib/validate-usdt-address';
import { useWithdrawal, type WithdrawalType } from '../../use-withdrawal';
import { getUsdtBalanceByNetwork, normalizeWithdrawalNetworkCode } from '../../lib/network-balance';
import { validateWithdrawalForm } from '../../lib/validation';
import {
  describeWithdrawalAddressError,
  formatMaxWithdrawalAmount,
  getManualWithdrawalCap,
  sanitizeWithdrawalAddressInput,
  sanitizeWithdrawalAmountInput,
  type WithdrawalAddressFeedback,
} from '../../lib/manual-form';
import { parsePositiveDecimal } from '@shared/lib/parse-decimal';
import { useApiServices } from '@shared/lib/use-api-services';
import { swrKeys } from '@shared/api/swr/keys';
import { useSecondFactorAction } from '@features/security';
import {
  trackWithdrawalCreateFailed,
  trackWithdrawalCreateStarted,
  trackWithdrawalCreateSuccess,
} from '@shared/lib/analytics';
import { useWithdrawalsLocked, WithdrawalsLockBanner } from '../withdrawals-lock-banner';
import { UsdtOnlyNotice } from './usdt-only-notice';
import { WithdrawalNetworkField } from './withdrawal-network-field';
import { WithdrawalAddressField } from './withdrawal-address-field';
import { WithdrawalAmountField } from './withdrawal-amount-field';

interface WithdrawalModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type?: WithdrawalType;
  assetBalances: AssetBalanceDto[];
  walletBalance?: number;
  isBalanceLoading?: boolean;
  balanceError?: unknown;
  onRetryBalance?: () => void;
}

export function WithdrawalModal({
  open,
  onOpenChange,
  type = 'merchants',
  assetBalances,
  walletBalance,
  isBalanceLoading = false,
  balanceError,
  onRetryBalance,
}: WithdrawalModalProps) {
  const { t, i18n } = useTranslation('cabinet');
  const { toast } = useToast();
  const { prepareIntent, confirmIntent, sendIntentEmail, isLoading } = useWithdrawal(type);
  const withdrawalsLocked = useWithdrawalsLocked();
  const { user } = useAuth();
  const services = useApiServices();
  const { run } = useSecondFactorAction();
  const addressBook = useSWR(open ? swrKeys.security.withdrawalAddresses() : null, () =>
    services.security.getWithdrawalAddresses(),
  );
  const limits = useSWR<WithdrawalLimitsDto>(open ? swrKeys.withdrawals.limits() : null, () => services.withdrawals.getLimits());
  const activePage = useSWR(open ? swrKeys.withdrawals.all({ page: 1, limit: 50 }) : null);
  const walletKind = type === 'partners' ? 'partner' : 'merchant';
  const activeWithdrawal = useMemo(() => {
    const rows = ((activePage.data as { data?: WithdrawalUserDto[] } | undefined)?.data ?? []);
    return rows
      .map(mapWithdrawalDtoToEntity)
      .find((item) => item.walletKind === walletKind && isActiveWithdrawalState(item.state, item.status)) ?? null;
  }, [activePage.data, walletKind]);
  const pendingSession = open ? readIntentSession(user?.id != null ? String(user.id) : undefined) : null;
  const [formError, setFormError] = useState<string | null>(null);

  const [form, setForm] = useState({ network: '', address: '', amount: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [addressFeedback, setAddressFeedback] = useState<WithdrawalAddressFeedback | null>(null);
  const [addressTouched, setAddressTouched] = useState(false);

  const networkBalance = useMemo(
    () => getUsdtBalanceByNetwork(assetBalances, form.network),
    [assetBalances, form.network],
  );
  const cap = useMemo(() => getManualWithdrawalCap(networkBalance), [networkBalance]);

  const hasBalanceError = Boolean(balanceError);
  const isBalancePending = isBalanceLoading && open;
  const balanceReady = !hasBalanceError && !isBalancePending;
  const availableUnknown = Boolean(form.network) && cap == null;
  const whitelistOn = true;
  const allLimit = limits.data?.profiles?.find((profile) => profile.scope === 'ALL');
  const isWithdrawDisabled =
    isLoading ||
    withdrawalsLocked ||
    isBalancePending ||
    hasBalanceError ||
    availableUnknown ||
    (balanceReady && (form.network ? cap != null && cap <= 0 : walletBalance != null && walletBalance <= 0))
    || Boolean(activeWithdrawal);

  const resetForm = useCallback(() => {
    setForm({ network: '', address: '', amount: '' });
    setErrors({});
    setAddressFeedback(null);
    setAddressTouched(false);
    setFormError(null);
  }, []);

  useEffect(() => {
    if (!open) resetForm();
  }, [open, resetForm]);

  useEffect(() => {
    if (!form.network || !form.amount.trim() || cap == null) return;

    setErrors((prev) => {
      const nextErrors = validateWithdrawalForm(
        { network: form.network, address: form.address, amount: form.amount },
        cap,
        t,
      );
      if (nextErrors.amount) return { ...prev, amount: nextErrors.amount };
      if (!prev.amount) return prev;
      const next = { ...prev };
      delete next.amount;
      return next;
    });
  }, [form.network, form.amount, form.address, cap, t]);

  const checkAddress = useCallback(
    async (address: string, network: string) => {
      if (!network || whitelistOn) {
        setAddressFeedback(null);
        return true;
      }
      const result = await validateUsdtAddress(address, network);
      if (!result.isValid && result.errorCode !== 'REQUIRED') {
        setAddressFeedback(describeWithdrawalAddressError(address, network, result.errorCode, t));
        return false;
      }
      setAddressFeedback(null);
      return result.isValid;
    },
    [t, whitelistOn],
  );

  const handleNetworkChange = (network: WithdrawalNetwork) => {
    const keepAddress =
      !whitelistOn ||
      (addressBook.data?.items ?? []).some(
        (item) =>
          item.address === form.address &&
          normalizeWithdrawalNetworkCode(item.network) === normalizeWithdrawalNetworkCode(network),
      );
    const nextAddress = keepAddress ? form.address : '';
    setForm((prev) => ({ ...prev, network, address: nextAddress }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.network;
      delete next.amount;
      return next;
    });
    if (!nextAddress) {
      setAddressFeedback(null);
      return;
    }
    if (addressTouched || addressFeedback) void checkAddress(nextAddress, network);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (hasBalanceError || isBalancePending || withdrawalsLocked) return;

    if (cap == null) return;
    const validationErrors = validateWithdrawalForm(form, cap, t);
    setAddressTouched(true);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
    }

    const addressOk = whitelistOn ? Boolean(form.address.trim()) : await checkAddress(form.address, form.network);
    const blocked = (addressBook.data?.items ?? []).some(
      (item) => item.amlBlocked && item.address === form.address.trim(),
    );
    if (blocked) {
      setFormError(t('security.addressAmlBlockedHint'));
      return;
    }
    if (!addressOk || Object.keys(validationErrors).length > 0) return;

    const amount = parsePositiveDecimal(form.amount).normalized;
    setFormError(null);

    trackWithdrawalCreateStarted();
    const prepared = await prepareIntent({
      network: form.network,
      address: form.address.trim(),
      amount,
      session: pendingSession,
    });
    if (!prepared.success) {
      trackWithdrawalCreateFailed('create_failed');
      setFormError(prepared.error);
      toast(prepared.error, 'error');
      return;
    }
    const outcome = await run({
      purpose: 'withdrawal.create',
      required: true,
      sendEmail: async () => {
        await sendIntentEmail(prepared.intentId);
      },
      request: (factor) => {
        if (!factor) return Promise.resolve({ success: false as const, error: t('withdrawal.modal.factorRequired') });
        return confirmIntent(prepared, factor);
      },
    });
    const result = outcome.ok
      ? outcome.data
      : { success: false as const, error: outcome.cancelled ? undefined : t('withdrawal.modal.error') };

    if (result.success) {
      trackWithdrawalCreateSuccess('created');
      toast(t('withdrawal.modal.accepted', { id: result.id }), 'success');
      onOpenChange(false);
      return;
    }

    trackWithdrawalCreateFailed('create_failed');
    if (result.error) {
      setFormError(result.error);
      toast(result.error, 'error');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span
              className="inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-secondary text-sm font-semibold text-foreground"
              aria-hidden
            >
              ₮
            </span>
            {t('withdrawal.modal.title')}
          </DialogTitle>
          <DialogDescription>{t('withdrawal.modal.description')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <WithdrawalsLockBanner />
          {activeWithdrawal ? (
            <StatusBanner>
              <div className="flex flex-col gap-2">
                <span>{t('withdrawal.modal.activeWithdrawal')}</span>
                <Button type="button" variant="outline" size="sm" className="w-full sm:w-auto" asChild>
                  <Link to={`/withdrawals?withdrawal=${activeWithdrawal.id}`}>{t('withdrawal.modal.openRequest')}</Link>
                </Button>
              </div>
            </StatusBanner>
          ) : null}
          {pendingSession ? (
            <p className="rounded-lg border border-border bg-accent/40 p-3 text-sm text-foreground">
              {t('withdrawal.modal.checking')}
            </p>
          ) : null}
          <UsdtOnlyNotice />
          <WithdrawalNetworkField
            network={form.network}
            assetBalances={assetBalances}
            error={errors.network}
            disabled={withdrawalsLocked}
            onChange={handleNetworkChange}
          />
          <WithdrawalAddressField
            network={form.network}
            address={form.address}
            whitelistEnabled={whitelistOn}
            items={addressBook.data?.items ?? []}
            addressesLoading={addressBook.isLoading}
            addressesError={Boolean(addressBook.error)}
            language={i18n.language}
            feedback={addressTouched ? addressFeedback : null}
            fieldError={errors.address}
            onChange={(value) => {
              const address = sanitizeWithdrawalAddressInput(value);
              setForm((prev) => ({ ...prev, address }));
              setErrors((prev) => {
                const next = { ...prev };
                delete next.address;
                return next;
              });
              if (addressTouched) void checkAddress(address, form.network);
            }}
            onBlur={() => {
              if (!form.address) return;
              setAddressTouched(true);
              void checkAddress(form.address, form.network);
            }}
            onSwitchNetwork={handleNetworkChange}
          />
          <WithdrawalAmountField
            amount={form.amount}
            network={form.network}
            cap={cap}
            error={errors.amount}
            isPending={isBalancePending}
            balanceError={balanceError}
            disabled={isBalancePending || hasBalanceError || withdrawalsLocked}
            onChange={(value) => setForm((prev) => ({ ...prev, amount: sanitizeWithdrawalAmountInput(value) }))}
            onMax={() => {
              if (cap == null) return;
              setForm((prev) => ({ ...prev, amount: formatMaxWithdrawalAmount(cap) }));
            }}
            onRetryBalance={onRetryBalance}
          />
          {allLimit ? (
            <p className="text-xs text-muted-foreground">
              {t('withdrawal.modal.limits', {
                single: allLimit.singleUsdt,
                daily: allLimit.dailyUsdt,
                monthly: allLimit.monthlyUsdt,
              })}
            </p>
          ) : null}
          {formError ? <p className="text-sm text-destructive">{formError}</p> : null}
          <Button type="submit" className="h-tap w-full sm:h-11" variant="default" loading={isLoading} disabled={isWithdrawDisabled}>
            {t('withdrawal.modal.button')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
